import { ReactElement, useState } from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useEventScoreUsgaPostLog from '../../dataHooks/useEventScoreUsgaPostLog';
import { USGA_POST_ACTIONS, USGA_POST_STATUSES } from '../../types/UsgaPost';

import type { UsgaPostLog, UsgaPostStatus } from '../../types/UsgaPost';

interface EventScoreUsgaPostLogProps {
  orgId: string;
  eventId: string;
  scoreId: string;
}

const OUTCOME_LABELS: Record<UsgaPostStatus, string> = {
  [USGA_POST_STATUSES.POSTING]: 'Posting',
  [USGA_POST_STATUSES.POSTED]: 'Posted',
  [USGA_POST_STATUSES.UNDER_REVIEW]: 'Under review',
  [USGA_POST_STATUSES.ERROR]: 'Error',
  [USGA_POST_STATUSES.UNKNOWN]: 'Unknown',
  [USGA_POST_STATUSES.WAITING_FOR_ACCESS]: 'Waiting for approval',
  [USGA_POST_STATUSES.ACCESS_NOT_GRANTED]: 'Approval not given',
};

const when = (t: UsgaPostLog['createdAt']): string =>
  t && 'toDate' in t
    ? t.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })
    : '';

/** The stored JSON string, indented for reading (or as stored, if it isn't JSON). */
const pretty = (json: string): string => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

function JsonBlock({ label, json }: { label: string; json: string }): ReactElement {
  return (
    <Stack>
      <Typography variant="labelSmall" color="text.secondary">{label}</Typography>
      <Typography
        component="pre"
        variant="bodySmall"
        sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
      >
        {pretty(json)}
      </Typography>
    </Stack>
  );
}

function LogEntry({ entry }: { entry: UsgaPostLog }): ReactElement {
  const [showJson, setShowJson] = useState(false);
  const hasJson = !!entry.requestJson || !!entry.responseJson;
  const action = entry.action === USGA_POST_ACTIONS.CONFIRM ? 'Confirm' : 'Post';
  const http = entry.httpStatus != null ? ` · HTTP ${entry.httpStatus}` : ' · not sent to the USGA';

  return (
    <Stack>
      <Typography variant="bodySmall">
        {when(entry.createdAt)} · {action} · {OUTCOME_LABELS[entry.outcome] ?? entry.outcome}{http}
      </Typography>
      {entry.message && <Typography variant="bodySmall" color="text.secondary">{entry.message}</Typography>}
      {hasJson && (
        <Button size="small" onClick={() => setShowJson((v) => !v)} sx={{ alignSelf: 'flex-start' }}>
          {showJson ? 'Hide request and response' : 'Show request and response'}
        </Button>
      )}
      {showJson && (
        <Stack spacing={1}>
          {entry.requestJson && <JsonBlock label="Request" json={entry.requestJson} />}
          {entry.responseJson && <JsonBlock label="Response" json={entry.responseJson} />}
        </Stack>
      )}
    </Stack>
  );
}

/** God only: a score's USGA posting history, loaded when its row is expanded. */
export default function EventScoreUsgaPostLog({ orgId, eventId, scoreId }: EventScoreUsgaPostLogProps): ReactElement {
  const { entries, isLoading, error } = useEventScoreUsgaPostLog(orgId, eventId, scoreId);

  if (isLoading) return <CircularProgress size="1.25em" />;
  if (error) return <Typography variant="bodySmall" color="error">Couldn&apos;t load the posting log.</Typography>;

  return (
    <Stack spacing={1}>
      <Typography variant="labelMedium">Posting log</Typography>
      {entries.length === 0
        ? <Typography variant="bodySmall" color="text.secondary">No attempts yet.</Typography>
        : entries.map((entry) => <LogEntry key={entry.itemId} entry={entry} />)}
    </Stack>
  );
}
